/**
 * Per-obstacle collision checks against the snake head.
 * Direct port of the Kotlin `collidesWithSnake` helper.
 */
import type { Obstacle } from './types';

const HEAD_RADIUS = 18;

function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** Helper for ChaosRing / ShiftingWall: is hx in the sliding gap at y oy? */
function isInShiftingGap(
  hx: number,
  hy: number,
  scrollY: number,
  oy: number,
  totalW: number,
  gapW: number,
  phase: number,
  speed: number,
  halfH: number,
): boolean {
  const inY = Math.abs(hy - oy) < halfH;
  if (!inY) return false;
  const t = scrollY * speed + phase;
  const center = totalW / 2 + Math.sin(t) * (totalW - gapW) * 0.35;
  const gs = Math.max(0, Math.min(center - gapW / 2, totalW - gapW));
  return hx >= gs && hx <= gs + gapW;
}

export function collidesWithSnake(o: Obstacle, hx: number, hy: number, scrollY: number): boolean {
  const headR = HEAD_RADIUS;
  switch (o.type) {
    case 'GATE': {
      const leftEnd = o.gapCenterX - o.gapWidth / 2;
      const rightStart = o.gapCenterX + o.gapWidth / 2;
      const halfH = 24;
      return Math.abs(hy - o.y) < halfH && (hx < leftEnd - headR || hx > rightStart + headR);
    }
    case 'PULSE_WALL': {
      const halfH = 24;
      const gs = o.gapStart;
      const ge = o.gapStart + o.gapWidth;
      return Math.abs(hy - o.y) < halfH && (hx < gs - headR || hx > ge + headR);
    }
    case 'DOUBLE_GATE': {
      const halfH = 24;
      const upper = Math.abs(hy - (o.y - o.spacing / 2)) < halfH;
      const lower = Math.abs(hy - (o.y + o.spacing / 2)) < halfH;
      if (!upper && !lower) return false;
      const leftEnd = o.gapCenterX - o.gapWidth / 2;
      const rightStart = o.gapCenterX + o.gapWidth / 2;
      return hx < leftEnd - headR || hx > rightStart + headR;
    }
    case 'LOG': {
      const cosR = Math.cos(o.rotation);
      const sinR = Math.sin(o.rotation);
      const dx = hx - o.x;
      const dy = hy - o.y;
      const lx = dx * cosR + dy * sinR;
      const ly = -dx * sinR + dy * cosR;
      return Math.abs(lx) < o.width / 2 + headR && Math.abs(ly) < o.height / 2 + headR;
    }
    case 'BEEHIVE': {
      const dx = hx - o.x;
      const dy = hy - o.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      return dist < o.ringRadius + headR;
    }
    case 'KELP_CURTAIN': {
      const halfH = 100;
      if (Math.abs(hy - o.y) >= halfH) return false;
      return !o.slits.some(([ls, le]) => hx >= ls - headR && hx <= le + headR);
    }
    case 'JELLYFISH': {
      const t = hy * 0.012 + o.driftPhase;
      const cx = o.x + Math.sin(t) * o.driftAmplitude;
      return distSq(hx, hy, cx, o.y) < (32 + headR) * (32 + headR);
    }
    case 'CORAL_SPIRE': {
      const halfH = o.height / 2;
      const inY = Math.abs(hy - o.y) < halfH;
      return inY && Math.abs(hx - o.x) < o.halfWidth + headR;
    }
    case 'PORTAL_WALL': {
      const halfH = 24;
      if (Math.abs(hy - o.y) >= halfH) return false;
      return !o.gaps.some(([gs, ge]) => hx >= gs - headR && hx <= ge + headR);
    }
    case 'CROSS_GATE': {
      const dx = hx - o.x;
      const dy = hy - o.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > o.armLength + headR) return false;
      const t = scrollY * o.rotationSpeed + o.phase;
      const armAngle = (t * (Math.PI / 2)) % (2 * Math.PI);
      const a = Math.atan2(dy, dx);
      let relAngle = (a - armAngle) % (2 * Math.PI);
      if (relAngle < 0) relAngle += 2 * Math.PI;
      const inArm = relAngle < Math.PI / 4 || relAngle > (7 * Math.PI) / 4;
      return inArm;
    }
    case 'DIAGONAL_BEAM': {
      const cosA = Math.cos(o.angle);
      const sinA = Math.sin(o.angle);
      const dx = hx - o.x;
      const dy = hy - o.y;
      const localX = dx * cosA + dy * sinA;
      const localY = -dx * sinA + dy * cosA;
      return Math.abs(localX) < o.length / 2 && Math.abs(localY) < o.thickness / 2 + headR;
    }
    case 'SHIFTING_WALL': {
      const t = scrollY * o.speed + o.phase;
      const center = o.totalWidth / 2 + Math.sin(t) * o.amplitude;
      const gs = Math.max(0, Math.min(center - o.gapWidth / 2, o.totalWidth - o.gapWidth));
      const ge = gs + o.gapWidth;
      const inY = Math.abs(hy - o.y) < 24;
      return inY && (hx < gs - headR || hx > ge + headR);
    }
    case 'CHAOS_RING': {
      const halfH = o.spacing / 2 + 24;
      if (Math.abs(hy - o.y) >= halfH) return false;
      const inUpper = isInShiftingGap(
        hx, hy, scrollY, o.y - o.spacing / 2,
        o.totalWidth, o.innerGapWidth, o.innerPhase, o.slideSpeed, 24,
      );
      const inLower = isInShiftingGap(
        hx, hy, scrollY, o.y + o.spacing / 2,
        o.totalWidth, o.outerGapWidth, o.outerPhase, o.slideSpeed, 24,
      );
      return (Math.abs(hy - (o.y - o.spacing / 2)) < 24 && !inUpper) ||
        (Math.abs(hy - (o.y + o.spacing / 2)) < 24 && !inLower);
    }
    case 'COMET': {
      const inY = Math.abs(hy - o.y) < 28;
      if (!inY) return false;
      const trailDx = -Math.sign(o.velocityX) * 60;
      const trailDist = Math.sqrt((hx - o.x + trailDx) ** 2 + (hy - o.y) ** 2);
      const bodyDist = Math.sqrt((hx - o.x) ** 2 + (hy - o.y) ** 2);
      return bodyDist < o.radiusBody + headR || trailDist < o.radiusBody + headR;
    }
    default:
      return false;
  }
}
